// ═══════════════════════════════════════════════════════════════════
// KEYBOARD SHORTCUTS HOOK — Global keyboard navigation
// Ctrl+K: Command palette
// ←/→: Previous/next page
// Escape: Close overlays
// ?: Show help
// 1-9: Jump to page by position
// ═══════════════════════════════════════════════════════════════════
import { useEffect, useCallback } from "react";
import { flattenNav, NAV, PERFIL_NAV, type NavItem } from "./navConfig.js";

// ── Type Interfaces ──────────────────────────────────

export interface KeyboardShortcutProps {
  setPage: ((pageId: string) => void) | null;
  onOpenCommandPalette?: (() => void) | null;
  onOpenShortcutsHelp?: (() => void) | null;
  onCloseOverlay?: (() => void) | null;
  currentPage: string;
}

export interface UseKeyboardShortcutsReturn {
  pageList: NavItem[];
  handleNavigatePage: (pageId: string) => void;
}

export function useKeyboardShortcuts({
  setPage,
  onOpenCommandPalette,
  onOpenShortcutsHelp,
  onCloseOverlay,
  currentPage,
}: KeyboardShortcutProps): UseKeyboardShortcutsReturn {
  // Flatten navigation to get ordered list of pages
  const pageList: NavItem[] = [
    ...flattenNav(NAV),
    PERFIL_NAV, // Profile is last
  ];

  const navigateToPage = useCallback(
    (pageId: string): void => {
      if (setPage) setPage(pageId);
    },
    [setPage]
  );

  const findPageIndex = useCallback((): number => {
    return pageList.findIndex((p) => p.id === currentPage);
  }, [pageList, currentPage]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      // Don't intercept if user is typing in an input
      const target = event.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.contentEditable === "true";

      // Ctrl+K (Cmd+K on Mac): Open command palette
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        if (onOpenCommandPalette) onOpenCommandPalette();
      }

      // ?: Show shortcuts help
      if (event.key === "?") {
        event.preventDefault();
        if (onOpenShortcutsHelp) onOpenShortcutsHelp();
      }

      // Escape: Close overlay
      if (event.key === "Escape") {
        if (onCloseOverlay) onCloseOverlay();
      }

      // Skip navigation shortcuts if in input
      if (isInput) return;

      // ← / →: Navigate pages
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        const currentIndex = findPageIndex();
        if (currentIndex > 0) {
          navigateToPage(pageList[currentIndex - 1].id);
        }
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        const currentIndex = findPageIndex();
        if (currentIndex < pageList.length - 1) {
          navigateToPage(pageList[currentIndex + 1].id);
        }
      }

      // 1-9: Quick jump by position
      const numKey = parseInt(event.key, 10);
      if (!isNaN(numKey) && numKey >= 1 && numKey <= 9) {
        const targetIndex = numKey - 1;
        if (targetIndex < pageList.length) {
          event.preventDefault();
          navigateToPage(pageList[targetIndex].id);
        }
      }
    },
    [
      findPageIndex,
      navigateToPage,
      onOpenCommandPalette,
      onOpenShortcutsHelp,
      onCloseOverlay,
      pageList,
    ]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Return helper functions and page list for use in components
  return {
    pageList,
    handleNavigatePage: navigateToPage,
  };
}
